import { z } from 'zod';
import {
  ChecklistItemStatusSchema,
  EngagementPhaseSchema,
  EngagementScoreSchema,
  EngagementStageSchema,
} from './engagement.js';

/**
 * Current `PhaseSnapshot.payload` schema revision (#2246 FR-5). Bumped whenever
 * the allowlist below changes shape; historical rows keep their own version so a
 * reader dispatches on `schemaVersion` rather than guessing.
 */
export const PHASE_SNAPSHOT_SCHEMA_VERSION = 1;

/**
 * @aiPurpose one CLOSING checklist item as frozen into a conversion snapshot
 *   (#2246 FR-5): its stable template key + status + active flag ONLY. No item
 *   id, no free text — the key identifies the row against the LOCKED template.
 * @aiInvariant `.strict()` — an unknown key (a stray blob, a note, a file
 *   reference) is REJECTED. The allowlist is the redaction boundary.
 */
export const PhaseSnapshotChecklistItemSchema = z
  .object({
    templateKey: z.string().min(1),
    status: ChecklistItemStatusSchema,
    active: z.boolean(),
  })
  .strict();
export type PhaseSnapshotChecklistItem = z.infer<typeof PhaseSnapshotChecklistItemSchema>;

/**
 * @aiPurpose the versioned, allowlisted `PhaseSnapshot.payload` shape written at
 *   the CLOSING→DEAL conversion (#2246 FR-5). It freezes the reconstructable
 *   card state at payment: engagement identity (`leadCode`/`dealCode`), phase +
 *   stage, bound client id, assignee id, qualification `score`, the checklist
 *   keys + statuses, and linked-artifact ids.
 * @aiInvariant `.strict()` is load-bearing: the payload stores ONLY the fields
 *   below. It must NEVER carry raw intake PII (phone/name/company), file bytes,
 *   object-storage keys, secrets, or free blobs — the strict allowlist REJECTS
 *   any such extra key at validation, and the writer parses the payload against
 *   this schema BEFORE insert. Ids/codes/enums only; the PII surface lives in the
 *   Client/lead rows, never copied here.
 * @aiPii ids + codes + enum phase/stage + numeric score only. No phone, no name,
 *   no company, no object key, no bytes — enforced by the allowlist, not by
 *   convention.
 */
export const PhaseSnapshotPayloadSchema = z
  .object({
    schemaVersion: z.literal(PHASE_SNAPSHOT_SCHEMA_VERSION),
    engagementId: z.string().uuid(),
    leadCode: z.string(),
    dealCode: z.string(),
    phase: EngagementPhaseSchema,
    stage: EngagementStageSchema,
    clientId: z.string().uuid(),
    assigneeId: z.string().uuid(),
    score: EngagementScoreSchema.nullable(),
    checklist: z.array(PhaseSnapshotChecklistItemSchema),
    artifactIds: z.array(z.string().uuid()),
  })
  .strict();
export type PhaseSnapshotPayload = z.infer<typeof PhaseSnapshotPayloadSchema>;
